import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSendEmail } from './useSendEmail';
import type { SendEmailModalProps } from './useSendEmailModal';
import { TemplateSaveOption } from '../components/TemplateUpdateConfirmationDialog';
import { showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { getTemplateIdByPageType, UI_CONSTANTS } from '../constants/emailConstants';

interface UseSendEmailModalWrapperProps {
  open: boolean;
  onClose: () => void;
  initialPageType: string;
  recipients: SendEmailModalProps['recipients'];
  onUpdateEmail: SendEmailModalProps['onUpdateEmail'];
  isEditRecipient: SendEmailModalProps['isEditRecipient'];
  setIsEditRecipient: SendEmailModalProps['setIsEditRecipient'];
  removeRecipient: SendEmailModalProps['removeRecipient'];
}

interface EmailProgress {
  sentEmails: number;
  totalEmails: number;
  message: string;
  progressPercentage: number;
}

export function useSendEmailModalWrapper({
  open,
  onClose,
  initialPageType,
  recipients,
  onUpdateEmail,
  isEditRecipient,
  setIsEditRecipient,
  removeRecipient,
}: UseSendEmailModalWrapperProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTemplateSaved, setIsTemplateSaved] = useState(false);
  const [customContentForThisEmail, setCustomContentForThisEmail] = useState<string | null>(null);
  const [resetTemplatePreview, setResetTemplatePreview] = useState(false);
  const [templatePreviewReady, setTemplatePreviewReady] = useState(false);
  const [emailSendingProgress, setEmailSendingProgress] = useState<EmailProgress>(UI_CONSTANTS.INITIAL_PROGRESS);

  const templateContentRef = useRef<string>('');
  const getTemplateContentFnRef = useRef<(() => string) | null>(null);
  const showUnsavedChangesPopupFnRef = useRef<(() => boolean) | null>(null);

  const {
    loading,
    pageType,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail: originalHandleSendEmail,
    handleCloseResponseSummary: originalHandleCloseResponseSummary,
    responseSummary,
    handleCloseWrapperModal: originalHandleCloseWrapperModal,
  } = useSendEmail({
    initialPageType,
    recipients: recipients || [],
    onClose,
    onProgress: setEmailSendingProgress,
    getTemplateContent: () => getTemplateContentFnRef.current?.() || templateContentRef.current,
  });

  const templateId = useMemo(() => getTemplateIdByPageType(initialPageType), [initialPageType]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setHasUnsavedChanges(false);
      setIsTemplateSaved(false);
      setCustomContentForThisEmail(null);
      setResetTemplatePreview(false);
      setTemplatePreviewReady(false);
      templateContentRef.current = '';
      getTemplateContentFnRef.current = null;
      showUnsavedChangesPopupFnRef.current = null;
      setEmailSendingProgress(UI_CONSTANTS.INITIAL_PROGRESS);
    }
  }, [open]);

  const handlePreviewReady = useCallback((ready: boolean) => {
    setTemplatePreviewReady(ready);
  }, []);

  const handleCloseWrapperModal = useCallback(() => {
    setResetTemplatePreview(true);
    setCustomContentForThisEmail(null);
    setTimeout(() => setResetTemplatePreview(false), UI_CONSTANTS.RESET_TIMEOUT);
    originalHandleCloseWrapperModal();
  }, [originalHandleCloseWrapperModal]);

  // Reset custom content after successful send (when response summary is closed)
  const handleCloseResponseSummary = useCallback(() => {
    // Reset custom content for "this email only" after successful send
    setCustomContentForThisEmail(null);
    setResetTemplatePreview(true);
    setTimeout(() => setResetTemplatePreview(false), UI_CONSTANTS.RESET_TIMEOUT);
    originalHandleCloseResponseSummary();
  }, [originalHandleCloseResponseSummary]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    // Prevent tab change if there are unsaved changes
    if (hasUnsavedChanges && showUnsavedChangesPopupFnRef.current?.()) {
      return;
    }
    
    if (newValue === 1 && getTemplateContentFnRef.current) {
      const content = getTemplateContentFnRef.current();
      templateContentRef.current = content;
    }
    setActiveTab(newValue);
  }, [hasUnsavedChanges]);

  const handleTemplateSaveStatusChange = useCallback((saved: boolean) => {
    setIsTemplateSaved(saved);
    if (saved) {
      setHasUnsavedChanges(false);
    }
  }, []);

  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const handleGetCurrentTemplateContent = useCallback((getContent: () => string) => {
    getTemplateContentFnRef.current = getContent;
    const content = getContent();
    templateContentRef.current = content;
  }, []);

  const handleGetShowUnsavedChangesPopup = useCallback((showFunction: () => boolean) => {
    showUnsavedChangesPopupFnRef.current = showFunction;
  }, []);

  const handleConfirmTemplateUpdate = useCallback(
    (option: TemplateSaveOption) => {
      if (getTemplateContentFnRef.current) {
        const content = getTemplateContentFnRef.current();
        templateContentRef.current = content;

        if (option === 'this_email_only') {
          setCustomContentForThisEmail(content);
        } else if (option === 'save_permanently') {
          setIsTemplateSaved(true);
        }
      }
      setHasUnsavedChanges(false);
      setActiveTab(1);
    },
    []
  );

  const handleCancelTemplateUpdate = useCallback(() => {
    showWarningSnackbar('Template changes have been discarded. The original template content will be used.');
  }, []);

  const handleNextClick = useCallback(() => {
    if (hasUnsavedChanges && showUnsavedChangesPopupFnRef.current?.()) {
      return;
    }
    if (getTemplateContentFnRef.current) {
      templateContentRef.current = getTemplateContentFnRef.current();
    }
    setActiveTab(1);
  }, [hasUnsavedChanges]);

  const handleSendClick = useCallback(() => {
    if (hasUnsavedChanges && showUnsavedChangesPopupFnRef.current?.()) {
      return;
    }
    if (getTemplateContentFnRef.current) {
      templateContentRef.current = getTemplateContentFnRef.current();
    }
    setEmailSendingProgress({
      sentEmails: 0,
      totalEmails: recipients?.length || 0,
      message: 'Preparing to send emails...',
      progressPercentage: 0,
    });
    originalHandleSendEmail();
  }, [hasUnsavedChanges, recipients?.length, originalHandleSendEmail]);

  const showDateRange = useMemo(
    () => (pageType === 'customer' || pageType === 'supplier') && activeTab === 1,
    [pageType, activeTab]
  );

  return {
    // State
    activeTab,
    hasUnsavedChanges,
    isTemplateSaved,
    templatePreviewReady,
    customContentForThisEmail,
    resetTemplatePreview,
    emailSendingProgress,
    templateId,
    showDateRange,
    // Email Hook State
    loading,
    pageType,
    dateRange,
    modalHeader,
    responseSummary,
    // Handlers
    handleTabChange,
    handleCloseWrapperModal,
    handleTemplateSaveStatusChange,
    handleUnsavedChangesChange,
    handleGetCurrentTemplateContent,
    handleGetShowUnsavedChangesPopup,
    handleConfirmTemplateUpdate,
    handleCancelTemplateUpdate,
    handlePreviewReady,
    handleNextClick,
    handleSendClick,
    handleDateRangeChange,
    handleCloseResponseSummary,
    setCustomContentForThisEmail,
    // Props
    recipients,
    onUpdateEmail,
    isEditRecipient,
    setIsEditRecipient,
    removeRecipient,
  };
}

